/**
 * Service API de base
 * Gestion des appels HTTP vers le backend
 */

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  details?: unknown;
  mustChangePassword?: boolean;
  emailSent?: boolean;
}

export class ApiService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_URL || 'http://localhost:3000';
  }

  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  /**
   * Déconnexion forcée - efface les tokens et redirige vers la page de connexion
   */
  private forceLogout(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Rediriger vers la page de connexion
    window.location.href = '/login';
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...(options.headers as Record<string, string>),
    };

    try {
      let response = await fetch(url, {
        ...options,
        headers,
      });

      // Si 401 et qu'on a un refresh token, essayer de rafraîchir
      // On ne refresh pas pour login, refresh, register, logout (mais on refresh pour /auth/me)
      const noRefreshEndpoints = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/logout'];
      const shouldSkipRefresh = noRefreshEndpoints.some(e => endpoint.includes(e));
      if (response.status === 401 && !shouldSkipRefresh) {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResponse = await fetch(`${this.baseUrl}/api/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            // Backend retourne { tokens: { accessToken, refreshToken, expiresIn } }
            const newAccessToken = refreshData.tokens?.accessToken || refreshData.accessToken;
            const newRefreshToken = refreshData.tokens?.refreshToken || refreshData.refreshToken;

            if (!newAccessToken) {
              this.forceLogout();
              return { error: 'Session expirée. Veuillez vous reconnecter.' };
            }

            // Sauvegarder les nouveaux tokens
            localStorage.setItem('accessToken', newAccessToken);
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }

            // Réessayer la requête originale avec le nouveau token
            const newHeaders = {
              ...headers,
              Authorization: `Bearer ${newAccessToken}`,
            };

            response = await fetch(url, {
              ...options,
              headers: newHeaders,
            });
          } else {
            // Refresh token invalide - déconnexion forcée
            this.forceLogout();
            return { error: 'Session expirée. Veuillez vous reconnecter.' };
          }
        } else {
          // Pas de refresh token - déconnexion forcée
          this.forceLogout();
          return { error: 'Session expirée. Veuillez vous reconnecter.' };
        }
      }

      const data = await response.json();

      if (!response.ok) {
        return {
          error: data.error || 'Une erreur est survenue',
          details: data.details,
          mustChangePassword: data.mustChangePassword,
          emailSent: data.emailSent,
        };
      }

      return { data };
    } catch (error) {
      console.error('API Error:', error);
      return {
        error: 'Erreur de connexion au serveur',
      };
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  get baseURL(): string {
    return this.baseUrl;
  }

  /**
   * Telecharge un fichier (Excel, PDF, etc.) avec authentification
   */
  async downloadFile(endpoint: string, defaultFilename?: string): Promise<void> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      ...this.getAuthHeader(),
    };

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Extraire le nom du fichier depuis les headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const contentType = response.headers.get('Content-Type') || '';

      // Determiner l'extension par defaut selon le Content-Type
      let defaultExt = '.xlsx';
      if (contentType.includes('pdf')) {
        defaultExt = '.pdf';
      } else if (contentType.includes('csv')) {
        defaultExt = '.csv';
      }

      let filename = defaultFilename || `download${defaultExt}`;

      if (contentDisposition) {
        // Essayer plusieurs patterns pour extraire le filename
        // Pattern 1: filename="nom.xlsx"
        let matches = /filename="([^"]+)"/.exec(contentDisposition);
        if (!matches) {
          // Pattern 2: filename=nom.xlsx
          matches = /filename=([^;\s]+)/.exec(contentDisposition);
        }
        if (!matches) {
          // Pattern 3: filename*=UTF-8''nom.xlsx
          matches = /filename\*=(?:UTF-8'')?([^;\s]+)/i.exec(contentDisposition);
        }
        if (matches && matches[1]) {
          filename = decodeURIComponent(matches[1].replace(/['"]/g, ''));
        }
      }

      // Telecharger le fichier
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download Error:', error);
      throw error;
    }
  }
}

// Instance par défaut
export const api = new ApiService();
